class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    if (this.shouldBindCartIcon()) {
      this.setHeaderCartIconAccessibility();
    }
    this.recoContext = null;
    this.atcEffectTimeout = null;
    this.pendingAtcEffectVariantId = null;
    this.recoAnimationTimeout = null;
    this.recoStartDelayTimeout = null;
    this.rewardAnimationTimeouts = [];
    this.cartUpdateUnsubscriber = null;
    this.pointerCloseRequested = false;
    this.pointerCloseResetTimeout = null;
    this.scrollGuardsActive = false;
    this.scrollGuardTouchX = null;
    this.scrollGuardTouchY = null;
    this.onPointerCloseIntent = this.handlePointerCloseIntent.bind(this);
    this.onScrollGuardTouchStart = this.handleScrollGuardTouchStart.bind(this);
    this.onScrollGuardTouchMove = this.handleScrollGuardTouchMove.bind(this);
    this.onScrollGuardWheel = this.handleScrollGuardWheel.bind(this);
    this.addEventListener('pointerdown', this.onPointerCloseIntent, true);
    this.addEventListener('mousedown', this.onPointerCloseIntent, true);
    this.addEventListener('touchstart', this.onPointerCloseIntent, { capture: true, passive: true });
  }

  connectedCallback() {
    this.applyButtonRules();
    if (this.cartUpdateUnsubscriber) return;
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event || !event.cartData) return;
      this.setCartMeta(event.cartData);
      this.applyButtonRules();
    });
  }

  disconnectedCallback() {
    this.disableScrollGuards();
    if (this.pointerCloseResetTimeout) {
      clearTimeout(this.pointerCloseResetTimeout);
      this.pointerCloseResetTimeout = null;
    }

    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
      this.cartUpdateUnsubscriber = null;
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  shouldBindCartIcon() {
    const cartType = document.documentElement?.dataset?.cartType;
    return !cartType || cartType === 'drawer';
  }

  handlePointerCloseIntent(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.id !== 'CartDrawer-Overlay' && !target.closest('.drawer__close, [data-cart-drawer-action="continue"]')) {
      return;
    }

    this.pointerCloseRequested = true;
    if (this.pointerCloseResetTimeout) clearTimeout(this.pointerCloseResetTimeout);
    this.pointerCloseResetTimeout = setTimeout(() => {
      this.pointerCloseRequested = false;
      this.pointerCloseResetTimeout = null;
    }, 1000);
  }

  shouldSuppressRestoredFocusRing(event) {
    const suppress = this.pointerCloseRequested || event?.type === 'click' || !!event?.pointerType;
    this.pointerCloseRequested = false;
    if (this.pointerCloseResetTimeout) {
      clearTimeout(this.pointerCloseResetTimeout);
      this.pointerCloseResetTimeout = null;
    }
    return suppress;
  }

  suppressRestoredFocusRing(element) {
    if (!element?.classList) return;
    element.classList.add('focus-restored-by-pointer');
    const removeSuppression = () => {
      element.classList.remove('focus-restored-by-pointer');
      element.removeEventListener('blur', removeSuppression);
      element.removeEventListener('keydown', removeSuppression);
    };
    element.addEventListener('blur', removeSuppression);
    element.addEventListener('keydown', removeSuppression);
    requestAnimationFrame(() => {
      if (document.activeElement !== element) removeSuppression();
    });
  }

  setRecoContext(context) {
    this.recoContext = context;
  }

  getRecoContext() {
    return this.recoContext || 'cart';
  }

  getRecoVisibility() {
    const visibility = this.dataset.recoVisibility || 'on_add_and_in_cart';
    return {
      visibility,
      showOnAdd: visibility === 'on_add' || visibility === 'on_add_and_in_cart',
      showInCart: visibility === 'in_cart' || visibility === 'on_add_and_in_cart',
    };
  }

  getRecoMode() {
    return this.dataset.recoMode || 'per_item';
  }

  shouldExpandRecoOnAdd() {
    return this.dataset.recoExpandOnAdd !== 'false';
  }

  hydrateDeferredImages() {
    this.querySelectorAll('img[data-card-deferred-image="true"][data-src]').forEach((image) => {
      const src = image.getAttribute('data-src');
      const srcset = image.getAttribute('data-srcset');
      const sizes = image.getAttribute('data-sizes');

      if (src) image.setAttribute('src', src);
      if (srcset) image.setAttribute('srcset', srcset);
      if (sizes) image.setAttribute('sizes', sizes);

      image.removeAttribute('data-src');
      image.removeAttribute('data-srcset');
      image.removeAttribute('data-sizes');
      image.removeAttribute('data-card-deferred-image');
    });
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
      this.setRecoContext('cart');
    }
    this.hydrateDeferredImages();
    this.applyButtonRules();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    if (this.skipRecoCheck) {
      this.skipRecoCheck = false;
    } else {
      this.hideEmptyRecommendations();
    }
    this.enableScrollGuards();
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
        if (this.pendingAtcEffectVariantId) {
          this.triggerAtcEffect({ id: this.pendingAtcEffectVariantId });
          this.pendingAtcEffectVariantId = null;
        }
      },
      { once: true }
    );

    if (typeof window.lockBodyScroll === 'function') {
      window.lockBodyScroll();
    } else {
      document.body.classList.add('overflow-hidden');
    }

    if (this.pendingAtcEffectVariantId) {
      setTimeout(() => {
        if (this.pendingAtcEffectVariantId) {
          this.triggerAtcEffect({ id: this.pendingAtcEffectVariantId });
          this.pendingAtcEffectVariantId = null;
        }
      }, 450);
    }
  }

  close(event) {
    const suppressRestoredFocusRing = this.shouldSuppressRestoredFocusRing(event);
    this.disableScrollGuards();
    this.classList.remove('active');
    this.clearRewardAnimation();
    if (suppressRestoredFocusRing) this.suppressRestoredFocusRing(this.activeElement);
    removeTrapFocus(this.activeElement);
    if (typeof window.unlockBodyScroll === 'function') {
      window.unlockBodyScroll();
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }

  enableScrollGuards() {
    if (this.scrollGuardsActive) return;
    this.addEventListener('touchstart', this.onScrollGuardTouchStart, { passive: true });
    this.addEventListener('touchmove', this.onScrollGuardTouchMove, { passive: false });
    this.addEventListener('wheel', this.onScrollGuardWheel, { passive: false });
    this.scrollGuardsActive = true;
  }

  disableScrollGuards() {
    if (!this.scrollGuardsActive) return;
    this.removeEventListener('touchstart', this.onScrollGuardTouchStart);
    this.removeEventListener('touchmove', this.onScrollGuardTouchMove);
    this.removeEventListener('wheel', this.onScrollGuardWheel);
    this.scrollGuardTouchX = null;
    this.scrollGuardTouchY = null;
    this.scrollGuardsActive = false;
  }

  handleScrollGuardTouchStart(event) {
    if (!this.classList.contains('active') || !event.touches || event.touches.length === 0) return;
    this.scrollGuardTouchX = event.touches[0].clientX;
    this.scrollGuardTouchY = event.touches[0].clientY;
  }

  handleScrollGuardTouchMove(event) {
    if (!this.classList.contains('active') || !event.cancelable) return;
    if (!event.touches || event.touches.length !== 1) return;

    const currentX = event.touches[0].clientX;
    const currentY = event.touches[0].clientY;
    const previousX = typeof this.scrollGuardTouchX === 'number' ? this.scrollGuardTouchX : currentX;
    const previousY = typeof this.scrollGuardTouchY === 'number' ? this.scrollGuardTouchY : currentY;
    const deltaX = previousX - currentX;
    const deltaY = previousY - currentY;
    this.scrollGuardTouchX = currentX;
    this.scrollGuardTouchY = currentY;

    if (this.shouldPreventScrollGuardEvent(event.target, { deltaX, deltaY })) {
      event.preventDefault();
    }
  }

  handleScrollGuardWheel(event) {
    if (!this.classList.contains('active') || !event.cancelable) return;

    if (
      this.shouldPreventScrollGuardEvent(event.target, {
        deltaX: event.deltaX || 0,
        deltaY: event.deltaY || 0,
      })
    ) {
      event.preventDefault();
    }
  }

  shouldPreventScrollGuardEvent(target, deltas = {}) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (!drawerInner) return false;

    const eventTarget = target instanceof Element ? target : null;
    if (!eventTarget || !drawerInner.contains(eventTarget)) return true;

    const deltaX = Number(deltas.deltaX || 0);
    const deltaY = Number(deltas.deltaY || 0);
    const axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
    const scrollContainer = this.getScrollableContainer(eventTarget, drawerInner, axis);
    if (!scrollContainer) return true;
    if (deltaX === 0 && deltaY === 0) return false;

    return !this.canScrollContainer(scrollContainer, { deltaX, deltaY }, axis);
  }

  getScrollableContainer(startNode, boundaryNode, axis = 'y') {
    let node = startNode;
    while (node && node !== boundaryNode) {
      if (this.isScrollableElement(node, axis)) return node;
      node = node.parentElement;
    }

    return this.isScrollableElement(boundaryNode, axis) ? boundaryNode : null;
  }

  isScrollableElement(node, axis = 'y') {
    if (!(node instanceof HTMLElement)) return false;
    const styles = window.getComputedStyle(node);
    if (axis === 'x') {
      const overflowX = styles.overflowX;
      const canOverflowX = overflowX === 'auto' || overflowX === 'scroll' || overflowX === 'overlay';
      return canOverflowX && node.scrollWidth > node.clientWidth + 1;
    }

    const overflowY = styles.overflowY;
    const canOverflowY = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
    return canOverflowY && node.scrollHeight > node.clientHeight + 1;
  }

  canScrollContainer(node, deltas = {}, axis = 'y') {
    if (!(node instanceof HTMLElement)) return false;
    if (axis === 'x') {
      const deltaX = Number(deltas.deltaX || 0);
      if (deltaX < 0) return node.scrollLeft > 0;
      if (deltaX > 0) return node.scrollLeft + node.clientWidth < node.scrollWidth - 1;
      return true;
    }

    const deltaY = Number(deltas.deltaY || 0);
    if (deltaY < 0) return node.scrollTop > 0;
    if (deltaY > 0) return node.scrollTop + node.clientHeight < node.scrollHeight - 1;
    return true;
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, { open = true } = {}) {
    this.setAttribute('aria-busy', 'true');
    this.classList.remove('is-empty');
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.classList.remove('is-empty');
    const drawerItems = this.querySelector('cart-drawer-items');
    if (drawerItems) drawerItems.classList.remove('is-empty');

    this.productId = parsedState.id;
    this.clearRecoAnimation();
    this.clearRewardAnimation();
    if (parsedState && parsedState.id) {
      this.pendingAtcEffectVariantId = String(parsedState.id);
    }
    this.setRecoContext('add');
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const sectionHtml = parsedState.sections[section.id];
      if (!sectionHtml) return;
      const sectionMarkup = this.getSectionInnerHTML(sectionHtml, section.selector);

      if (section.id === 'cart-icon-bubble' && typeof window.updateCartIconBubbleCount === 'function') {
        window.updateCartIconBubbleCount(sectionMarkup);
        return;
      }

      sectionElement.innerHTML = sectionMarkup;
    });
    this.setCartMeta(parsedState);
    this.setCartMetaFromSections(parsedState);

    let recoStartDelay = 0;
    if (this.pendingAtcEffectVariantId) {
      this.prepareAtcReveal(this.pendingAtcEffectVariantId);
      recoStartDelay = this.prepareRewardAnimationSequence();
      this.prepareRecoAnimation(this.pendingAtcEffectVariantId, { startDelay: recoStartDelay });
    }

    const finalizeRender = () => {
      this.hideEmptyRecommendations();
      this.applyButtonRules();
      this.skipRecoCheck = open;
      if (open) this.open();
      this.removeAttribute('aria-busy');
    };

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(finalizeRender);
    } else {
      setTimeout(finalizeRender, 0);
    }
  }

  setCartMetaFromSections(parsedState) {
    const sectionHtml = parsedState?.sections?.['cart-drawer'];
    if (!sectionHtml) return;
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const drawerRoot = doc.querySelector('#CartDrawer');
    if (!drawerRoot) return;

    const itemCount = Number(drawerRoot.dataset.cartItemCount || this.dataset.cartItemCount || 0);
    const totalPrice = Number(drawerRoot.dataset.cartTotalPrice || this.dataset.cartTotalPrice || 0);
    this.setCartMeta({ item_count: itemCount, total_price: totalPrice });
  }

  setCartMeta(cartData) {
    if (!cartData) return;
    const itemCount = Number(cartData.item_count ?? cartData.itemCount ?? this.dataset.cartItemCount ?? 0);
    const totalPrice = Number(cartData.total_price ?? cartData.totalPrice ?? this.dataset.cartTotalPrice ?? 0);
    this.dataset.cartItemCount = String(itemCount);
    this.dataset.cartTotalPrice = String(totalPrice);

    const drawerRoot = this.querySelector('#CartDrawer');
    if (drawerRoot) {
      drawerRoot.dataset.cartItemCount = String(itemCount);
      drawerRoot.dataset.cartTotalPrice = String(totalPrice);
    }
  }

  applyButtonRules() {
    const hasActions = this.querySelector('[data-cart-drawer-action]');
    if (!hasActions) return;

    const minAdded = Number(this.dataset.btnMinAddedPrice || 0);
    const minCount = Number(this.dataset.btnMinItemCount || 0);
    const minTotal = Number(this.dataset.btnMinTotalPrice || 0);
    const logic = (this.dataset.btnConditionLogic || 'and').toLowerCase();
    const cartItemCount = Number(this.dataset.cartItemCount || 0);
    const cartTotalPrice = Number(this.dataset.cartTotalPrice || 0);

    const context = this.getRecoContext();
    const addedActive = context === 'add' && minAdded > 0;
    const countActive = minCount > 0;
    const totalActive = minTotal > 0;

    let meetsAdded = true;
    if (addedActive) {
      const addedLinePrice = this.getAddedLinePrice();
      meetsAdded = addedLinePrice >= Math.round(minAdded * 100);
    }

    const meetsCount = !countActive || cartItemCount >= minCount;
    const meetsTotal = !totalActive || cartTotalPrice >= Math.round(minTotal * 100);

    let allowed = meetsAdded && meetsCount && meetsTotal;
    if (logic === 'or') {
      const checks = [];
      if (addedActive) checks.push(meetsAdded);
      if (countActive) checks.push(meetsCount);
      if (totalActive) checks.push(meetsTotal);
      allowed = checks.length ? checks.some(Boolean) : true;
    }

    this.applyDrawerActionStyle('view', allowed ? this.dataset.btnViewTrue : this.dataset.btnViewFalse);
    this.applyDrawerActionStyle('checkout', allowed ? this.dataset.btnCheckoutTrue : this.dataset.btnCheckoutFalse);
    this.applyDrawerActionStyle('continue', allowed ? this.dataset.btnContinueTrue : this.dataset.btnContinueFalse);
  }

  getAddedLinePrice() {
    if (!this.pendingAtcEffectVariantId) return 0;
    const row = this.querySelector(`.cart-item[data-variant-id="${this.pendingAtcEffectVariantId}"]`);
    return Number(row?.dataset?.linePrice || 0);
  }

  applyDrawerActionStyle(action, style) {
    const button = this.querySelector(`[data-cart-drawer-action="${action}"]`);
    if (!button) return;

    const normalized = style || 'none';
    if (normalized === 'none') {
      button.setAttribute('hidden', 'hidden');
      button.classList.add('hidden');
      if (action === 'checkout') {
        const checkoutForm = button.closest('form');
        if (checkoutForm) {
          checkoutForm.setAttribute('hidden', 'hidden');
          checkoutForm.classList.add('hidden');
        }
      }
      return;
    }

    button.removeAttribute('hidden');
    button.classList.remove('hidden');
    if (action === 'checkout') {
      const checkoutForm = button.closest('form');
      if (checkoutForm) {
        checkoutForm.removeAttribute('hidden');
        checkoutForm.classList.remove('hidden');
      }
    }

    button.className = this.getDrawerActionClasses(action, normalized);
  }

  getDrawerActionClasses(action, style) {
    const base = action === 'checkout' ? 'cart__checkout-button cart__drawer-action' : 'cart__drawer-action';
    switch (style) {
      case 'primary':
        return `${base} button button--primary`;
      case 'secondary':
        return `${base} button button--secondary`;
      case 'link':
        return `${base} link button-label`;
      default:
        return `${base} button button--primary`;
    }
  }

  hideEmptyRecommendations() {
    const { visibility, showOnAdd, showInCart } = this.getRecoVisibility();
    const context = this.getRecoContext();
    const wrappers = this.querySelectorAll('[data-reco-wrapper]');
    const hideWrapper = (wrapper) => {
      wrapper.classList.add('hidden');
      const row = wrapper.closest('[data-reco-row]');
      if (row) row.classList.add('hidden');
    };

    const shouldShowInContext = context === 'add' ? showOnAdd : showInCart;
    if (!shouldShowInContext) {
      wrappers.forEach((wrapper) => hideWrapper(wrapper));
      return;
    }

    let allowedWrapper = null;
    if (context === 'add' && visibility === 'on_add') {
      const mode = this.getRecoMode();
      if (mode === 'combined') {
        allowedWrapper = this.querySelector('.cart-item__recommendations--combined[data-reco-wrapper]');
      } else if (this.pendingAtcEffectVariantId) {
        const row = this.querySelector(
          `[data-reco-row][data-reco-variant-id="${this.pendingAtcEffectVariantId}"]`
        );
        allowedWrapper = row ? row.querySelector('[data-reco-wrapper]') : null;
      }
      if (!allowedWrapper) {
        wrappers.forEach((wrapper) => hideWrapper(wrapper));
        return;
      }
    }

    wrappers.forEach((wrapper) => {
      if (context === 'add' && allowedWrapper && wrapper !== allowedWrapper) {
        hideWrapper(wrapper);
        return;
      }
      const list = wrapper.querySelector('[data-reco-list]');
      if (!list) return;
      const row = wrapper.closest('[data-reco-row]');
      const productRecommendations = wrapper.querySelector('product-recommendations');
      const fallbackList = wrapper.querySelector('[data-fallback-list]');
      const getRecoItemCount = () =>
        Array.from(list.children).filter(
          (item) =>
            item.tagName === 'LI' &&
            item.hasAttribute('data-product-recommendations-anchor') === false
        ).length;
      const hasItems = () => getRecoItemCount() > 0;
      const shouldRequestRecommendations = () => {
        if (!productRecommendations || productRecommendations.dataset.recoRequested) return false;
        const limit = Number.parseInt(productRecommendations.dataset.productsLimit, 10);
        if (!Number.isFinite(limit) || limit <= 0) return true;
        return getRecoItemCount() < limit;
      };
      const show = () => {
        wrapper.classList.remove('hidden');
        if (row) row.classList.remove('hidden');
        if (wrapper.dataset.recoAnimatePending === 'true') {
          this.activateRecoAnimation(wrapper);
        }
      };
      const hide = () => {
        wrapper.classList.add('hidden');
        if (row) row.classList.add('hidden');
      };

      // Hide immediately until we confirm there are items to show.
      hide();
      if (shouldRequestRecommendations()) {
        productRecommendations.dataset.recoRequested = 'true';
        const load = () => {
          if (typeof productRecommendations.loadRecommendations === 'function') {
            productRecommendations.loadRecommendations(productRecommendations.dataset.productId);
          }
        };
        if (typeof productRecommendations.loadRecommendations === 'function') {
          load();
        } else if (window.customElements && typeof customElements.whenDefined === 'function') {
          customElements.whenDefined('product-recommendations').then(load);
        }
      }

      if (hasItems()) {
        show();
        return;
      }

      if (!productRecommendations) {
        hide();
        return;
      }

      let observer = null;
      const timeout = setTimeout(() => {
        if (!hasItems()) hide();
        if (observer) observer.disconnect();
      }, 2000);

      const tryShow = () => {
        if (hasItems()) {
          clearTimeout(timeout);
          if (observer) observer.disconnect();
          show();
        }
      };

      observer = new MutationObserver(tryShow);
      observer.observe(list, { childList: true });
      if (fallbackList) observer.observe(fallbackList, { childList: true });

      productRecommendations.addEventListener('product-recommendations--loaded', tryShow, { once: true });
      setTimeout(tryShow, 50);
    });
  }

  prepareAtcReveal(variantId) {
    if (!['option_1', 'confetti_pop', 'confetti_fall'].includes(this.dataset.atcEffect)) return;
    const revealClass = 'cart-item--atc-reveal';
    this.querySelectorAll(`.${revealClass}`).forEach((element) => element.classList.remove(revealClass));
    const target = this.querySelector(`.cart-item[data-variant-id="${variantId}"]`) || this.querySelector('.cart-item');
    if (target) target.classList.add(revealClass);
  }

  prepareRewardAnimationSequence() {
    const rewards = Array.from(this.querySelectorAll('[data-cart-reward]'));
    if (!rewards.length) return 0;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 0;

    const revealDelay = 220;
    const progressKickoffDelay = revealDelay + 180;
    const progressDuration = 860;
    const recoDelay = progressKickoffDelay + progressDuration + 160;

    rewards.forEach((reward) => {
      reward.classList.remove('cart-reward--atc-visible', 'cart-reward--atc-progress');
      reward.classList.add('cart-reward--atc-sequenced');
      reward.style.setProperty('--cart-reward-atc-progress-duration', `${progressDuration}ms`);

      const bar = reward.querySelector('.cart-reward__bar');
      if (bar) {
        bar.style.setProperty('--cart-reward-progress', '0%');
      }

      reward.querySelectorAll('.cart-reward__segment-fill[data-segment-fill]').forEach((segmentFill) => {
        segmentFill.style.width = '0%';
      });

      const revealTimeout = setTimeout(() => {
        if (reward.isConnected) reward.classList.add('cart-reward--atc-visible');
      }, revealDelay);
      this.rewardAnimationTimeouts.push(revealTimeout);

      const progressTimeout = setTimeout(() => {
        if (!reward.isConnected) return;

        reward.classList.add('cart-reward--atc-progress');

        if (bar) {
          const progressTarget = this.getRewardPercentValue(reward.dataset.rewardProgress);
          bar.style.setProperty('--cart-reward-progress', `${progressTarget}%`);
        }

        reward.querySelectorAll('.cart-reward__segment-fill[data-segment-fill]').forEach((segmentFill) => {
          const segmentTarget = this.getRewardPercentValue(segmentFill.dataset.segmentFill);
          segmentFill.style.width = `${segmentTarget}%`;
        });
      }, progressKickoffDelay);
      this.rewardAnimationTimeouts.push(progressTimeout);
    });

    return recoDelay;
  }

  clearRewardAnimation() {
    if (this.rewardAnimationTimeouts.length) {
      this.rewardAnimationTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      this.rewardAnimationTimeouts = [];
    }

    this.querySelectorAll('[data-cart-reward]').forEach((reward) => {
      reward.classList.remove('cart-reward--atc-sequenced', 'cart-reward--atc-visible', 'cart-reward--atc-progress');
      reward.style.removeProperty('--cart-reward-atc-progress-duration');

      const bar = reward.querySelector('.cart-reward__bar');
      if (bar) {
        const progressTarget = this.getRewardPercentValue(reward.dataset.rewardProgress);
        bar.style.setProperty('--cart-reward-progress', `${progressTarget}%`);
      }

      reward.querySelectorAll('.cart-reward__segment-fill[data-segment-fill]').forEach((segmentFill) => {
        const segmentTarget = this.getRewardPercentValue(segmentFill.dataset.segmentFill);
        segmentFill.style.width = `${segmentTarget}%`;
      });
    });
  }

  getRewardPercentValue(value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return 0;
    return Math.min(100, Math.max(0, numeric));
  }

  prepareRecoAnimation(variantId, { startDelay = 0 } = {}) {
    this.clearRecoAnimation();
    if (!this.shouldExpandRecoOnAdd()) return;
    if (!variantId) return;
    const wrapper = this.getRecoWrapperForVariant(variantId);
    if (!wrapper) return;
    wrapper.dataset.recoAnimatePending = 'true';
    if (startDelay > 0) {
      wrapper.dataset.recoAnimateDelayMs = String(startDelay);
    }
    if (!wrapper.classList.contains('hidden')) {
      this.activateRecoAnimation(wrapper);
    }
  }

  activateRecoAnimation(wrapper) {
    if (!wrapper) return;
    delete wrapper.dataset.recoAnimatePending;
    if (!this.shouldExpandRecoOnAdd()) {
      delete wrapper.dataset.recoAnimateActive;
      delete wrapper.dataset.recoAnimateDelayMs;
      delete wrapper.dataset.recoAnimateDelayed;
      return;
    }
    if (wrapper.dataset.recoAnimateActive === 'true') return;

    const openRecommendation = () => {
      if (!wrapper.isConnected) return;

      delete wrapper.dataset.recoAnimateDelayMs;
      delete wrapper.dataset.recoAnimateDelayed;
      wrapper.dataset.recoAnimateActive = 'true';

      this.querySelectorAll('.cart-reco-details').forEach((details) => {
        if (!wrapper.contains(details)) details.open = false;
      });

      const details = wrapper.querySelector('.cart-reco-details');
      if (details) details.open = true;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        delete wrapper.dataset.recoAnimateActive;
        wrapper.classList.remove('reco-animate-target');
        return;
      }

      wrapper.classList.remove('reco-animate-target');
      const startReveal = () => {
        if (wrapper.isConnected) wrapper.classList.add('reco-animate-target');
      };

      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(startReveal);
      } else {
        startReveal();
      }

      if (this.recoAnimationTimeout) {
        clearTimeout(this.recoAnimationTimeout);
      }
      this.recoAnimationTimeout = setTimeout(() => {
        if (wrapper.isConnected) wrapper.classList.remove('reco-animate-target');
        delete wrapper.dataset.recoAnimateActive;
        this.recoAnimationTimeout = null;
      }, 260);
    };

    const startDelay = Number(wrapper.dataset.recoAnimateDelayMs || 0);
    if (startDelay > 0) {
      if (wrapper.dataset.recoAnimateDelayed === 'true') return;
      wrapper.dataset.recoAnimateDelayed = 'true';
      if (this.recoStartDelayTimeout) {
        clearTimeout(this.recoStartDelayTimeout);
      }
      this.recoStartDelayTimeout = setTimeout(() => {
        this.recoStartDelayTimeout = null;
        openRecommendation();
      }, startDelay);
      return;
    }

    openRecommendation();
  }

  clearRecoAnimation() {
    if (this.recoStartDelayTimeout) {
      clearTimeout(this.recoStartDelayTimeout);
      this.recoStartDelayTimeout = null;
    }
    if (this.recoAnimationTimeout) {
      clearTimeout(this.recoAnimationTimeout);
      this.recoAnimationTimeout = null;
    }
    this.querySelectorAll('[data-reco-wrapper]').forEach((wrapper) => {
      wrapper.classList.remove('reco-animate-target');
      if (wrapper.dataset) {
        delete wrapper.dataset.recoAnimatePending;
        delete wrapper.dataset.recoAnimateActive;
        delete wrapper.dataset.recoAnimateDelayMs;
        delete wrapper.dataset.recoAnimateDelayed;
      }
    });
  }

  getRecoWrapperForVariant(variantId) {
    if (!variantId) return null;
    const row = this.querySelector(`[data-reco-row][data-reco-variant-id="${variantId}"]`);
    if (row) {
      const wrapper = row.querySelector('[data-reco-wrapper]');
      if (wrapper) return wrapper;
    }

    const item = this.querySelector(`.cart-item[data-variant-id="${variantId}"]`);
    if (item) {
      const nextRow = item.nextElementSibling;
      if (nextRow && nextRow.matches('[data-reco-row]')) {
        const wrapper = nextRow.querySelector('[data-reco-wrapper]');
        if (wrapper) return wrapper;
      }
    }

    return (
      this.querySelector('.cart-item__recommendations--combined[data-reco-wrapper]') ||
      this.querySelector('[data-reco-wrapper]')
    );
  }

  triggerAtcEffect(parsedState) {
    if (!['option_1', 'confetti_pop', 'confetti_fall'].includes(this.dataset.atcEffect)) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const revealClass = 'cart-item--atc-reveal';
    const variantId = parsedState && parsedState.id ? String(parsedState.id) : null;

    this.classList.remove('atc-effect-active');
    if (this.atcEffectTimeout) {
      clearTimeout(this.atcEffectTimeout);
      this.atcEffectTimeout = null;
    }

    this.querySelectorAll(`.${revealClass}`).forEach((element) => element.classList.remove(revealClass));

    let target = null;
    if (variantId) {
      target = this.querySelector(`.cart-item[data-variant-id="${variantId}"]`);
    }
    if (!target) {
      target = this.querySelector('.cart-item');
    }
    if (target) target.classList.add(revealClass);

    const activate = () => this.classList.add('atc-effect-active');
    if (prefersReducedMotion) {
      activate();
    } else {
      requestAnimationFrame(() => activate());
    }

    this.atcEffectTimeout = setTimeout(() => {
      this.classList.remove('atc-effect-active');
      if (target) target.classList.remove(revealClass);
    }, prefersReducedMotion ? 400 : 800);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
